import { ComponentEmotionWheelLevels } from '@/graphql/generated/graphql';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useEmotionWheelStore } from '../store/useEmotionWheelStore';
import { trackEmotionLogged } from '@/lib/ga-custom-events/emotion';
import { getTimestamp } from '@/lib/ga-custom-events/utils';

export function useCircleContainer() {
  const [positionWheel, setPositionWheel] = useState(-90);
  const { emotions, L1, L2, onUpdateL1, onUpdateL2, slices, onUpdateSlices } = useEmotionWheelStore();
  const [startDate, setStartDate] = useState<number>(0);
  const sliceCount = slices.length;
  const radius = 160;
  const gradient1From = L1?.attributes?.typeSection.mainGradientFrom;
  const center = radius;
  const sliceAngle = 360 / sliceCount;
  const textRadius = radius * 0.6;

  useEffect(() => {
    setPositionWheel(-90);
  }, [slices]);

  const onClickSlice = async (startAngle: number, index: number) => {
    const targetAngle = -(startAngle + sliceAngle / 2) + 270;
    setPositionWheel(targetAngle);
    if (!L1) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const L2Emotions = emotions[index].attributes?.L2 || [];
      onUpdateL1(emotions[index]);
      setStartDate(new Date().getTime());
      onUpdateSlices(L2Emotions as ComponentEmotionWheelLevels[], emotions[index]);
      setPositionWheel(-90);
    }

    if (L1 && !L2) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onUpdateL2(L1?.attributes?.L2?.[index] as ComponentEmotionWheelLevels);
      const endDate = new Date().getTime();
      trackEmotionLogged({
        emotionL1: L1?.attributes?.emotion,
        emotionL2: L1?.attributes?.L2?.[index]?.emotion,
        timeSpent: getTimestamp(startDate, endDate),
      });
    }
  };

  const generateGradients = () => {
    return slices.map((gradient, index) => {
      const startAngle = sliceAngle * index;
      const endAngle = sliceAngle * (index + 1);

      const newCenter = center;
      const middle = (startAngle + endAngle) / 2;

      const gradientTransform = `rotate(${middle + 90} ${newCenter} ${newCenter})`;

      return (
        <linearGradient
          key={gradient.id}
          id={gradient.id}
          gradientUnits="userSpaceOnUse"
          x1={newCenter}
          y1={newCenter}
          x2={newCenter}
          y2={newCenter - radius}
          gradientTransform={gradientTransform}>
          <stop offset="0%" stopColor={gradient.end} />
          <stop offset="100%" stopColor={gradient.start} />
        </linearGradient>
      );
    });
  };
  const generateSlices = () => {
    return Array.from({ length: sliceCount }).map((_, index) => {
      const startAngle = sliceAngle * index;
      const endAngle = sliceAngle * (index + 1);
      const gradientId = `url(#${slices[index % slices.length].id})`;

      return (
        <motion.path
          key={index + (slices[index].emotion || '')}
          onClick={() => onClickSlice(startAngle, index)}
          d={`
                M${center},${center}
                L${center + radius * Math.cos((Math.PI * startAngle) / 180)},
                  ${center + radius * Math.sin((Math.PI * startAngle) / 180)}
                A${radius},${radius} 0 0,1
                  ${center + radius * Math.cos((Math.PI * endAngle) / 180)},
                  ${center + radius * Math.sin((Math.PI * endAngle) / 180)}
                Z
              `}
          fill={gradientId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1, duration: 1 }}
        />
      );
    });
  };

  const generateText = () => {
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    };

    const describeArc = (
      x: number,
      y: number,
      radius: number,
      startAngle: number,
      endAngle: number,
      sweepFlag: string,
    ) => {
      const start = polarToCartesian(x, y, radius, endAngle - 2);
      const end = polarToCartesian(x, y, radius, startAngle);

      const largeArcFlag = Math.abs(endAngle - 2 - startAngle) <= 180 ? '0' : '1';

      const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, sweepFlag, end.x, end.y].join(' ');

      return d;
    };

    return Array.from({ length: sliceCount }).map((_, index) => {
      const rotate: { [key: number]: number } = {
        5: 35,
        4: 0,
        3: -63,
        2: -179,
      };
      const angle = sliceAngle * (index + 1) + sliceAngle / 2;

      // Adjust the text radius as needed
      const curvedTextRadius = textRadius + 40;

      const angleExtent = sliceAngle;
      let startAngle = (rotate[sliceCount] || 0) + angle - angleExtent / 2;
      let endAngle = angle + angleExtent / 2;

      // Default sweep flag
      let sweepFlag = '0';

      if (index === 0 || index === slices.length - 1) {
        [startAngle, endAngle] = [endAngle, startAngle];
        sweepFlag = '1';
      }

      const pathId = `textPath${slices[index].emotion}`;
      const rotateAngle = sliceCount === 6 ? 30 : 0;
      const pathData = describeArc(
        center,
        center,
        curvedTextRadius,
        startAngle + rotateAngle,
        endAngle + rotateAngle,
        sweepFlag,
      );

      return (
        <React.Fragment key={slices[index].emotion}>
          <defs>
            <path id={pathId} d={pathData} stroke="blue" strokeWidth="2" fill="none" />
          </defs>
          <text
            onClick={() => onClickSlice(sliceAngle * index, index)}
            style={{
              fontSize: '14px',
              fill: 'black',
              fontWeight: '500',
            }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
              {`${slices[index].emotion}`}
            </textPath>
          </text>
        </React.Fragment>
      );
    });
  };

  return {
    positionWheel,
    emotions,
    L2,
    onUpdateSlices,
    radius,
    generateGradients,
    generateSlices,
    generateText,
    gradient1From,
  };
}
